/**
 * Tokeniser for the CIRCAD v6 keyword format.
 *
 * Every data line has the shape
 *
 *     Keyword:: Field:value Field:value ...
 *
 * Values are unquoted and may themselves contain spaces (`Name:Pad Master`),
 * so we cannot simply split on whitespace. Instead we scan for the next
 * ` Word:` field marker and take everything before it as the current value —
 * which is exactly how the original's sequential field scanner behaves.
 *
 * Two fields are terminal: `Data:` and `Name:`. The original breaks out of its
 * field loop on `Data:` (OG7.cpp:2463), so its payload may legally contain
 * colons and is taken verbatim to end of line.
 */

export interface CadRecord {
  /** Keyword without the trailing `::`, e.g. `Line`, `Pad`, `EndComp`. */
  keyword: string;
  /** Fields in written order. Duplicate keys are possible, so this is a list. */
  fields: { key: string; value: string }[];
  /** 1-based source line, for error messages. */
  line: number;
}

/** Field markers whose value extends to end of line. */
const TERMINAL_FIELDS = new Set(['Data']);

const FIELD_RE = /(?:^|\s)([A-Za-z][A-Za-z0-9]*):/g;

/**
 * Split one record line into keyword and fields.
 * Returns null for blank lines and for lines with no `::` keyword.
 */
export function parseRecordLine(raw: string, line: number): CadRecord | null {
  const text = raw.replace(/[\r\n]+$/, '');
  if (!text.trim()) return null;

  const kw = /^\s*([A-Za-z][A-Za-z0-9]*)::/.exec(text);
  if (!kw) return null;

  const keyword = kw[1]!;
  const rest = text.slice(kw[0].length);
  return { keyword, fields: splitFields(rest), line };
}

/** Split the field portion of a record into key/value pairs. */
export function splitFields(rest: string): { key: string; value: string }[] {
  const fields: { key: string; value: string }[] = [];

  FIELD_RE.lastIndex = 0;
  let match = FIELD_RE.exec(rest);
  while (match) {
    const key = match[1]!;
    const valueStart = match.index + match[0].length;

    if (TERMINAL_FIELDS.has(key)) {
      fields.push({ key, value: rest.slice(valueStart) });
      break;
    }

    // A quoted value ends at its closing quote; scanning for the next field
    // marker inside it would split on colons that belong to the string.
    if (rest[valueStart] === '"') {
      const close = rest.indexOf('"', valueStart + 1);
      const end = close === -1 ? rest.length : close + 1;
      fields.push({ key, value: rest.slice(valueStart, end) });
      FIELD_RE.lastIndex = end;
      match = FIELD_RE.exec(rest);
      continue;
    }

    FIELD_RE.lastIndex = valueStart;
    const next = FIELD_RE.exec(rest);
    const end = next ? next.index : rest.length;
    fields.push({ key, value: rest.slice(valueStart, end).trim() });
    match = next;
  }

  return fields;
}

/** First value for a key, or undefined. */
export function field(rec: CadRecord, key: string): string | undefined {
  return rec.fields.find((f) => f.key === key)?.value;
}

/** All values for a key, in order. */
export function fieldAll(rec: CadRecord, key: string): string[] {
  return rec.fields.filter((f) => f.key === key).map((f) => f.value);
}

/** Strip surrounding double quotes, if present. */
export function unquote(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const t = v.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) return t.slice(1, -1);
  return t;
}

/**
 * Parse a colour field. The original writes a `+`/`-` sign before the hex
 * digits (OG7.cpp:2804) where `-` marks the negative/knockout flag.
 */
export function parseColor(v: string | undefined): { rgb: number; negative: boolean } | undefined {
  if (!v) return undefined;
  const m = /^([+-]?)([0-9A-Fa-f]{6})$/.exec(v.trim());
  if (!m) return undefined;
  return { rgb: parseInt(m[2]!, 16), negative: m[1] === '-' };
}

/**
 * Split a point list. Points are `x,y` and are separated by `;`:
 * `Vertices:.3,-.215;.3,-.3`, `Bounds:x1,y1;x2,y2`.
 */
export function splitPoints(v: string): string[][] {
  return v
    .split(';')
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map((p) => p.split(',').map((c) => c.trim()));
}

/** Split a comma-separated list, e.g. `Layers:2,3,4`. */
export function splitList(v: string | undefined): string[] {
  if (!v) return [];
  return v.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
}
